import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, ViewChild, inject } from '@angular/core';
import {BehaviorSubject, combineLatest, Observable, of, Subject, Subscription} from 'rxjs';
import {
    determineWidthAndHeight,
    isLocalProjectReferencePhoto,
    isRemoteProjectReferencePhoto,
    ProjectReferencePhoto,
    ProjectReferencePhotoType
} from '../../../models/project-reference-photo';
import {ProjectReferencePhotoService} from '../../../services/project-reference-photo.service';
import {ToastrService} from 'ngx-toastr';
import {Mode} from '../../../models/mode';
import {hasInvalidFileExtension, hasInvalidFileSize} from '../../../utils/file';
import {VwuiModalService} from '@bvgo-ict/vwui-angular';
import {LightboxModalComponent} from '../../lightbox-modal/lightbox-modal.component';
import {SortableEvent, SortableOptions} from 'sortablejs';
import {PhotoPositionsRequest} from '../../../models/photo-positions.request';
import {catchError, debounceTime, map, switchMap} from 'rxjs/operators';
import createJustifiedLayout from 'justified-layout';
import {ProjectReferenceDetailResponse} from '../../../models/project-reference-detail.response';

type JustifiedLayoutItem = number | { width: number; height: number };

@Component({
    selector: 'app-prd-photos',
    templateUrl: './prd-photos.component.html',
    styleUrls: ['./prd-photos.component.scss'],
    standalone: false
})
export class PrdPhotosComponent implements OnDestroy, AfterViewInit {
    private projectReferencePhotoService = inject<ProjectReferencePhotoService>('ProjectReferencePhotoService' as any);
    private vwuiModalService = inject(VwuiModalService);
    private toast = inject(ToastrService);

    @Input() mode$: Observable<Mode> | undefined;
    @Output() primaryUpdated = new EventEmitter<void>();
    @ViewChild('widthMeasure') widthMeasure: ElementRef<HTMLDivElement>|undefined;

    private containerWidth$ = new BehaviorSubject<number>(-1);

    photoReferenceList$ = new BehaviorSubject<ProjectReferencePhotoType[]>([]);
    justifiedLayout$ = combineLatest([this.photoReferenceList$, this.containerWidth$]).pipe(
        switchMap(async ([photoReferenceList, containerWidth]) => {
            // Determine width and heights of photo references
            const widthAndHeights = await Promise.all(photoReferenceList.map(item => {
                if (isLocalProjectReferencePhoto(item)) {
                    return determineWidthAndHeight(item);
                }
                if (isRemoteProjectReferencePhoto(item)) {
                    const {width, height} = item;
                    return Promise.resolve({width, height});
                }

                console.warn('Unable to determine width and height for photo reference', item);
                return Promise.reject('Unable to determine width and height for photo reference');
            }));

            // Calculate the layout
            const justifiedLayout = createJustifiedLayout(widthAndHeights as JustifiedLayoutItem[], {
                containerWidth,
                containerPadding: 0,
                targetRowHeight: 150
            });

            // Merge photo reference list with the calculated layout
            return justifiedLayout.boxes.map((box, index) => {
                const currentPhotoReference = photoReferenceList[index];

                return {...currentPhotoReference, ...box};
            });
        })
    );
    cutoffHeight$ = this.justifiedLayout$.pipe(
        map(photoReferences => {
            const secondRowItem = photoReferences.find(item => item.top > 0);

            // Height is calculated as end of second row or height of first row
            return secondRowItem ? secondRowItem.top + secondRowItem.height : photoReferences.find(it => it.height > 0)?.height || 0;
        })
    );
    height$ = this.justifiedLayout$.pipe(
        map(photoReferences => {
            // Determines the height of the layout
            return photoReferences.reduce<number>((highestHeight, current) => {
                const height = current.top + current.height;

                if (height > highestHeight) {
                    return height;
                }

                return highestHeight;
            }, 0);
        })
    );

    primaryPhotoId: number | undefined;
    photoUploadQueueCount$ = new BehaviorSubject(0);
    fileAccept = '.jpg, .jpeg, .png';

    sortActive$ = new BehaviorSubject(false);

    sortUpdateSubject = new Subject<SortableEvent>();
    sortBackupPhotoReferenceList: ProjectReferencePhotoType[] = [];

    sortablejsOptions: SortableOptions = {
        handle: '.sortable',
        filter: '.unsortable',
        fallbackOnBody: true,
        animation: 250,
        onStart: () => {
            this.sortActive$.next(true);
        },
        onMove: e => {
            return e.related.className.indexOf('unsortable') === -1;
        },
        onUpdate: e => {
            this.sortUpdateSubject.next(e);
        },
        onEnd: () => {
            this.sortActive$.next(false);
        }
    };

    // tslint:disable-next-line:variable-name
    _projectReferenceId: number | undefined;
    _projectReferenceName: string | null | undefined;

    subscriptions: Subscription[] = [];

    constructor() {
        this.initSortUpdate();
    }

    @HostListener('window:resize')
    onResize() {
        this.determineContainerWidth()
    }

    ngAfterViewInit(): void {
        this.determineContainerWidth();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    @Input()
    set projectReference(reference: ProjectReferenceDetailResponse) {
        this._projectReferenceId = reference.id;
        this._projectReferenceName = reference.name;
        this.refreshPhotoList();
    }

    onPhotoSelect(files: File[]): void {
        if (hasInvalidFileSize(files)) {
            this.toast.error('Eén of meerdere foto\'s zijn ongeldig, de maximale bestandsgrootte is 50Mb')
            return;
        }

        if (hasInvalidFileExtension(files, this.fileAccept)) {
            this.toast.error('Eén of meerdere foto\'s zijn ongeldig, alleen de bestandsnaamextensies .jpg en .png zijn toegestaan')
            return;
        }

        let photoPosition = this.photoReferenceList$.value.length - 1;

        const newPhotos: ProjectReferencePhotoType[] = files.map(file => {
            photoPosition = photoPosition + 1;

            return {
                localFile: file,
                position: photoPosition
            };
        });

        this.photoUploadQueueCount$.next(newPhotos.length);
        this.photoReferenceList$.next([...this.photoReferenceList$.value, ...newPhotos]);
    }

    async onPhotoCreated(): Promise<void> {
        this.photoUploadQueueCount$.next(this.photoUploadQueueCount$.value - 1);

        if (this.photoUploadQueueCount$.value === 0) {
            await this.refreshPhotoList();
        }
    }

    async onPhotoUploadFailed(): Promise<void> {
        this.photoUploadQueueCount$.next(this.photoUploadQueueCount$.value - 1);
        await this.refreshPhotoList();
    }

    trackByPositionAndFocus(index: number, projectReferencePhoto: ProjectReferencePhotoType): string {
        if (isLocalProjectReferencePhoto(projectReferencePhoto)) {
            return `${projectReferencePhoto.position}-0-0` ;
        } else {
            return `${projectReferencePhoto.position}-${projectReferencePhoto.focusX}-${projectReferencePhoto.focusY}`;
        }
    }

    async refreshPhotoList(): Promise<void> {
        if (!this._projectReferenceId) {
            console.warn('Unable to refresh photo list');
            return;
        }

        try {
            const photos = await this.projectReferencePhotoService.getPhotos(this._projectReferenceId).toPromise();

            this.photoReferenceList$.next([...photos]);

            const newPrimaryPhotoId = photos.find(photo => photo.primary)?.id;
            if (this.primaryPhotoId !== undefined && newPrimaryPhotoId !== this.primaryPhotoId) {
                this.primaryUpdated.emit();
            }
            this.primaryPhotoId = newPrimaryPhotoId;
        } catch (e) {
            console.error('refreshPhotoList error', e);
            this.toast.error(`Verversen van foto's niet gelukt.`);
        }
    }

    openLightboxModal(photos: ProjectReferencePhotoType[], activeIndex: number): void {
        this.vwuiModalService.open(LightboxModalComponent, {
            data: {photos, activeIndex, projectName: this._projectReferenceName},
            modalClass: 'vwui-modal--lightbox'
        });
    }

    private initSortUpdate() {
        this.subscriptions.push(
            this.sortUpdateSubject.pipe(
                debounceTime(250),
                map(sortableEvent => {
                    const oldIndex = sortableEvent.oldIndex ? sortableEvent.oldIndex - 1 : -1;
                    const newIndex = sortableEvent.newIndex ? sortableEvent.newIndex - 1 : -1;

                    return this.updatePhotoPosition(
                        this.photoReferenceList$.value,
                        oldIndex,
                        newIndex
                    );
                }),
                switchMap(sortedList => {
                    return this.updatePhotoPositions(sortedList).pipe(
                        catchError(error => {
                            return this.updatePhotoPositionsError(error);
                        })
                    )
                })
            ).subscribe()
        )
    }

    private updatePhotoPosition(
        list: ProjectReferencePhotoType[],
        oldIndex: number,
        newIndex: number
    ): ProjectReferencePhotoType[] {
        if (
            (oldIndex !== undefined && oldIndex >= 0) &&
            (newIndex !== undefined && newIndex <= list.length - 1)
        ) {
            this.sortBackupPhotoReferenceList = list;

            const sortedList = [...list];
            const photo = list.find((_, index) => index === oldIndex);

            if (photo && isRemoteProjectReferencePhoto(photo)) {
                /** Move the photo object to the newIndex */
                sortedList.splice(oldIndex, 1);
                sortedList.splice(newIndex, 0, photo);
            }

            this.photoReferenceList$.next(sortedList);

            return sortedList;
        } else {
            return [];
        }
    }

    private updatePhotoPositions(sortedList: ProjectReferencePhotoType[]): Observable<ProjectReferencePhoto[]> {
        if (!this._projectReferenceId) {
            console.warn('Unable to sort photo positions');
            this.photoReferenceList$.next(this.sortBackupPhotoReferenceList);

            return of();
        }

        const positions: PhotoPositionsRequest[] = sortedList.map((photo, index) => {
            return {
                id: isRemoteProjectReferencePhoto(photo) ? photo.id : -1,
                position: index
            };
        }).filter(it => it.id !== -1);

        return this.projectReferencePhotoService.putPositions(this._projectReferenceId, positions)
    }

    private updatePhotoPositionsError(error: any) {
        console.error('updatePhotoPositions error', error);
        this.toast.error(`Sorteren van foto's mislukt`);
        this.photoReferenceList$.next(this.sortBackupPhotoReferenceList);

        return of()
    }

    private determineContainerWidth() {
        const containerWidth = this.widthMeasure?.nativeElement?.clientWidth;
        if (containerWidth) {
            this.containerWidth$.next(containerWidth);
        }
    }
}
